import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Users, Wifi, Car, Utensils, Star, Facebook, Instagram } from 'lucide-react';
import QuickAvailability from '../components/QuickAvailability';
import TestimonialCarousel from '../components/TestimonialCarousel';
import FeatureCard from '../components/FeatureCard';
import BookingLinks from '../components/BookingLinks';
import { heroImages, fallbackImages } from '../data/images';

const Home: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  // Use local images if available, fallback to Pexels images
  const imagesToUse = imageError ? fallbackImages.map((url, index) => ({
    url,
    title: heroImages[index]?.title || `Villa Image ${index + 1}`,
    subtitle: heroImages[index]?.subtitle || 'Beautiful villa experience'
  })) : heroImages;

  const features = [
    {
      icon: Users,
      title: 'Spacious Comfort',
      description: 'Accommodates up to 8 guests with 3 bedrooms and 2 bathrooms'
    },
    {
      icon: Wifi,
      title: 'Modern Amenities',
      description: 'Infinity Pool, High-speed WiFi, Smart TV, full kitchen, and climate control'
    },
    {
      icon: Car,
      title: 'Private Parking',
      description: 'Secure parking for 4 vehicles with easy access'
    },
    {
      icon: Utensils,
      title: 'Gourmet Kitchen',
      description: 'Fully equipped kitchen with premium appliances and dining area'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imagesToUse.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [imagesToUse.length]);

  const currentImage = imagesToUse[currentImageIndex];

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Sliding Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${currentImage.url}')`
            }}
          >
            <img
              src={currentImage.url}
              alt="Villa hero"
              className="hidden"
              onError={handleImageError}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-4"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6">
                {currentImage.title}
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto leading-relaxed">
                {currentImage.subtitle}
              </p>
            </motion.div>
          </AnimatePresence>
          
          <Link 
            to="/booking"
            className="inline-block bg-earth-gold hover:bg-earth-terracotta text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Book Your Stay
          </Link>

          <div className="mt-5 flex items-center justify-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=61593801283985"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Villa DINOOS on Facebook"
              className="rounded-full bg-white/15 p-3 text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white"
            >
              <Facebook className="h-6 w-6" />
            </a>
            <a
              href="https://www.instagram.com/villa_dinoos"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Villa DINOOS on Instagram"
              className="rounded-full bg-white/15 p-3 text-white transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white"
            >
              <Instagram className="h-6 w-6" />
            </a>
          </div>
        </motion.div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {imagesToUse.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentImageIndex 
                  ? 'bg-white scale-125' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black/20 z-20">
          <motion.div
            key={currentImageIndex}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 5, ease: 'linear' }}
            className="h-full bg-earth-gold"
          />
        </div>
      </section>

      {/* Quick Availability - Closed off for now until development is complete
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Check Availability
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find your perfect dates and start planning your escape to paradise
            </p>
          </motion.div>
          <QuickAvailability />
        </div>
      </section> */}

      {/* Booking Links Section */}
      <section className="py-16 bg-olive-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Reserve Your Stay
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Book through your preferred platform for a seamless reservation experience
            </p>
          </motion.div>
          <BookingLinks />
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-earth-beige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Villa DINOOS
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the perfect blend of luxury, comfort, and natural beauty
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              A Glimpse of Paradise
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Immerse yourself in the beauty and tranquility of our villa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              '/images/Night-Villa-view-1.png',
              '/images/Living-Space-AEB-View-1.png',
              '/images/Villa-Front-Night-view-1.jpg',
              '/images/IMG_5757.JPG',
              '/images/Villa-Garden-view-3.jpg',
              '/images/Villa-Pool-view-7.jpg',

            ].map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative overflow-hidden rounded-lg shadow-lg group cursor-pointer"
              >
                <img 
                  src={image} 
                  alt={`Villa interior ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    // Fallback to Pexels images if local images fail
                    const fallbackUrls = [
                      // 'https://images.pexels.com/photos/1743231/pexels-photo-1743231.jpeg?auto=compress&cs=tinysrgb&w=800',
                      // 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800',
                      // 'https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?auto=compress&cs=tinysrgb&w=800',
                      // 'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=800',
                      // 'https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=800',
                      // 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800'
                    ];
                    (e.target as HTMLImageElement).src = fallbackUrls[index] || fallbackUrls[0];
                  }}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/villa"
              className="inline-block bg-earth-brown hover:bg-earth-brown/90 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-earth-beige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              What Our Guests Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Read reviews from our satisfied guests who've experienced the magic of Villa DINOOS
            </p>
          </motion.div>
          <TestimonialCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-olive-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-4">
              Ready for Your Perfect Getaway?
            </h2>
            <p className="text-xl mb-8 text-olive-100">
              Book now and create memories that will last a lifetime
            </p>
            <Link 
              to="/booking"
              className="inline-block bg-earth-gold hover:bg-earth-terracotta text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Check Availability & Book
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
